/* eslint-disable max-lines */
import {
  Fragment,
  type FC,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MarkdownContent, type AiChatErrorCode } from "@9amhealth/shared";
import Link from "src/ui/components/Link/Link";
import { nineamComponentRegistry } from "./nineamComponentRegistry";
import {
  CheckmarkMessageDelivered,
  CheckmarkMessageSent
} from "src/constants/icons";
import AvatarCubit from "src/state/AvatarCubit/AvatarCubit";
import { useBloc } from "src/state/state";
import translate from "src/lib/translate";
import type { TranslationKey } from "src/types/translationKey";
import type {
  ChatPrompt,
  LeapChatMessage,
  MessageSendError
} from "./state/types";
import { formatMessageTime } from "./state/utils";
import ChatComposer from "./ChatComposer";
import { ToolCallSection } from "@9amhealth/shared";
import MessageFiles from "./MessageFiles";
import ChatDetailHeader from "./NavButton";
import {
  LeapChatContainer,
  LeapChatViewport,
  MessageListContent,
  AssistantMessageRoot,
  AssistantAvatar,
  AssistantMessageBody,
  AssistantMessageContent,
  UserMessageRoot,
  UserMessageContent,
  MessageFooter,
  FooterName,
  FooterError,
  FooterRetryLink,
  ComposerCardArea,
  MessageContainer,
  ThinkingText,
  InlinePromptRow,
  PromptButton,
  EscalationBanner,
  TextLink,
  OfflineIndicator
} from "./leapChat.styles";
import { useBlocNext } from "@blac/react";
import ConversationThreadCubit from "./state/ConversationThreadCubit";
import { featureFlags, FeatureFlagName } from "src/lib/featureFlags";

const linkComponents: Record<string, React.ElementType> = {
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <Link to={href}>{children}</Link>
  )
};

const errorTranslationKeys: Record<AiChatErrorCode, string> = {
  network: "leapChat.error.network",
  offline: "leapChat.error.offline",
  auth: "leapChat.error.auth",
  rate_limit: "leapChat.error.rateLimit",
  server_error: "leapChat.error.server",
  client_error: "leapChat.error.client",
  no_body: "leapChat.error.server",
  stream_error: "leapChat.error.stream",
  reply_error: "leapChat.error.replyError",
  unknown: "leapChat.error.unknown"
};

function getErrorMessage(error: MessageSendError): string {
  const key = errorTranslationKeys[error.code];
  return translate(key as TranslationKey);
}

const AssistantFooter: FC<{
  createdAt: number;
  senderName?: string;
  senderDescription?: string;
  avatarUrl: string;
  avatarAlt: string;
}> = ({ createdAt, senderName, senderDescription, avatarUrl, avatarAlt }) =>
  senderName ? (
    <MessageFooter>
      <AssistantAvatar>
        <img src={avatarUrl} alt={avatarAlt} />
      </AssistantAvatar>
      <FooterName>{senderName}</FooterName>
      {senderDescription ? ` · ${senderDescription}` : null}
      <span className="footer-time">{formatMessageTime(createdAt)}</span>
    </MessageFooter>
  ) : null;

const UserBubble: FC<{
  message: LeapChatMessage;
  onRetry?: () => void;
}> = memo(({ message, onRetry }) => (
  <UserMessageRoot>
    <UserMessageContent>
      {message.files && message.files.length > 0 && (
        <MessageFiles files={message.files} />
      )}
      {message.text && (
        <MarkdownContent
          text={message.text}
          components={linkComponents}
          customElements={nineamComponentRegistry}
        />
      )}
    </UserMessageContent>
    <MessageFooter className="user-footer">
      {message.error ? (
        <FooterError>
          {getErrorMessage(message.error)}{" "}
          <FooterRetryLink onClick={onRetry}>
            {translate("leapChat.sendErrorRetry" as TranslationKey)}
          </FooterRetryLink>
        </FooterError>
      ) : (
        <>
          {formatMessageTime(message.createdAt)}
          <span className="confirmation">
            {message.isLocal && message.localId === message.id ? (
              <CheckmarkMessageSent />
            ) : (
              <CheckmarkMessageDelivered />
            )}
          </span>
        </>
      )}
    </MessageFooter>
  </UserMessageRoot>
));

const AssistantBubble: FC<{
  message: LeapChatMessage;
  isStreaming: boolean;
  children?: React.ReactNode;
}> = memo(({ message, isStreaming, children }) => {
  const [avatar] = useBloc(AvatarCubit, {
    create: () => new AvatarCubit(message.senderId ?? "")
  });
  const avatarUrl = avatar.path ?? "/9amAvatar.png";

  return (
    <AssistantMessageRoot>
      <AssistantMessageBody>
        <AssistantMessageContent data-streaming={isStreaming ? "" : undefined}>
          {message.files && message.files.length > 0 && (
            <MessageFiles files={message.files} />
          )}
          {featureFlags.getFlag(FeatureFlagName.showChatToolCalls) &&
            message.toolCalls &&
            message.toolCalls.length > 0 && (
              <ToolCallSection toolCalls={message.toolCalls} />
            )}
          {message.error && !message.text ? (
            <FooterError>{getErrorMessage(message.error)}</FooterError>
          ) : isStreaming && !message.text ? (
            <ThinkingText>{translate("leapChat.thinking")}</ThinkingText>
          ) : (
            <MarkdownContent
              text={message.text}
              isStreaming={isStreaming}
              components={linkComponents}
              customElements={nineamComponentRegistry}
            />
          )}
          {children}
        </AssistantMessageContent>
        {message.error && message.text && (
          <FooterError>{getErrorMessage(message.error)}</FooterError>
        )}
        <AssistantFooter
          createdAt={message.createdAt}
          senderName={message.senderName}
          senderDescription={message.senderDescription}
          avatarUrl={avatarUrl}
          avatarAlt={message.senderName ?? "9am.health"}
        />
      </AssistantMessageBody>
    </AssistantMessageRoot>
  );
});

const UserMessageItem: FC<{
  message: LeapChatMessage;
  isLast: boolean;
  onRetry?: (id: string) => void;
}> = memo(({ message, isLast, onRetry }) => {
  const handleRetry = useCallback(
    () => onRetry?.(message.id),
    [onRetry, message.id]
  );

  return (
    <MessageContainer
      className="user-message"
      data-msg-id={message.id}
      data-msg-local={message.isLocal ? "" : undefined}
      data-msg-role="user"
      data-last={isLast ? "user" : undefined}
    >
      <UserBubble
        message={message}
        onRetry={message.error ? handleRetry : undefined}
      />
    </MessageContainer>
  );
});

const PromptIcon: FC<{ variant?: "primary" | "secondary" }> = ({ variant }) =>
  variant === "primary" ? (
    <svg viewBox="0 0 16 16" fill="none">
      <path
        d="M3 8.5l3.5 3.5L13 4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ) : (
    <svg viewBox="0 0 16 16" fill="none">
      <path
        d="M4 4l8 8M12 4l-8 8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );

const MessageList: FC<{
  messages: LeapChatMessage[];
  isRunning: boolean;
  isEscalated: boolean;
  escalationBannerIndex: number | null;
  prompts: Record<string, ChatPrompt>;
  onRetry: (id: string) => void;
  onSelectPromptOption: (messageId: string, value: string) => void;
  onStartNewConversation: () => void;
}> = memo(
  ({
    messages,
    isRunning,
    isEscalated,
    escalationBannerIndex,
    prompts,
    onRetry,
    onSelectPromptOption,
    onStartNewConversation
  }) => {
    const lastUserIdx = messages.findLastIndex((m) => m.role === "user");
    const lastAssistantIdx = messages.findLastIndex(
      (m) => m.role === "assistant"
    );
    const assistantIsLastMessage = lastAssistantIdx === messages.length - 1;

    const escalationBanner = escalationBannerIndex !== null && (
      <EscalationBanner key="escalation-banner">
        {translate("leapChat.conversation.escalated")} <br />
        <TextLink onClick={onStartNewConversation}>
          {translate("leapChat.conversation.start")}
        </TextLink>
      </EscalationBanner>
    );

    return (
      <>
        {messages.map((msg, idx) => {
          const isLastUser = idx === lastUserIdx;
          const isLastAssistant = idx === lastAssistantIdx;

          const banner =
            idx === escalationBannerIndex ? escalationBanner : null;

          if (msg.role === "user") {
            return (
              <Fragment key={msg.localId ?? msg.id}>
                {banner}
                <UserMessageItem
                  message={msg}
                  isLast={isLastUser}
                  onRetry={msg.error ? onRetry : undefined}
                />
              </Fragment>
            );
          }

          const prompt = prompts[msg.id];
          const showPromptButtons =
            prompt && (!isEscalated || prompt.selectedValue !== null);

          return (
            <Fragment key={msg.localId ?? msg.id}>
              {banner}
              <MessageContainer
                data-msg-id={msg.id}
                data-msg-local={msg.isLocal ? "" : undefined}
                data-msg-role="assistant"
                data-with-prompt={showPromptButtons ? "true" : "false"}
                data-last={
                  isLastAssistant && assistantIsLastMessage
                    ? "assistant"
                    : undefined
                }
                data-escalated={isEscalated ? "" : undefined}
                className="assistant-message"
              >
                <AssistantBubble
                  message={msg}
                  isStreaming={isRunning && idx === messages.length - 1}
                >
                  {showPromptButtons && (
                    <InlinePromptRow className="prompt-options">
                      {prompt.options.map((opt) => {
                        const isSelected = prompt.selectedValue === opt.value;
                        const isResolved = prompt.selectedValue !== null;
                        return (
                          <PromptButton
                            key={opt.value}
                            variant={opt.variant}
                            disabled={isResolved || isRunning}
                            data-selected={isSelected ? "" : undefined}
                            onClick={() =>
                              onSelectPromptOption(msg.id, opt.value)
                            }
                          >
                            <PromptIcon variant={opt.variant} />
                            {opt.label}
                          </PromptButton>
                        );
                      })}
                    </InlinePromptRow>
                  )}
                </AssistantBubble>
              </MessageContainer>
            </Fragment>
          );
        })}
        {escalationBannerIndex === messages.length && escalationBanner}
      </>
    );
  }
);

const ConversationDetailView: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [
    {
      messages,
      isInitializing,
      isRunning,
      activeUserMessageId,
      prompts,
      escalated,
      escalationBannerIndex,
      inputValue,
      attachments,
      isOffline
    },
    cubit
  ] = useBlocNext(ConversationThreadCubit, {
    id
  });

  useEffect(() => {
    if (id) {
      cubit.init(id);
    }
  }, [id, cubit]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);

  const handleRetry = useCallback(
    (msgId: string) => {
      cubit.retryMessage(msgId);
    },
    [cubit]
  );

  const handleSelectPromptOption = useCallback(
    (messageId: string, value: string) => {
      cubit.selectPromptOption(messageId, value);
    },
    [cubit]
  );

  useEffect(() => {
    cubit.setDomElements({
      scrollEl: scrollRef.current,
      contentEl: contentRef.current,
      composerEl: composerRef.current
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cubit, scrollRef.current, contentRef.current, composerRef.current]);

  useEffect(() => {
    if (!isInitializing && messages.length > 0) {
      cubit.scrollToBottom();
      requestAnimationFrame(() => cubit.scrollToBottom());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitializing]);

  useLayoutEffect(() => {
    cubit.updateNewMessagePositioning();
  }, [activeUserMessageId, cubit]);

  const startNewConversation = useCallback(() => {
    navigate("/app/chat/new");
  }, [navigate]);

  const handleSend = useCallback(() => {
    cubit.submitInput();
  }, [cubit]);

  if (isInitializing) {
    return <LeapChatContainer />;
  }

  return (
    <LeapChatContainer>
      <LeapChatViewport ref={scrollRef}>
        <ChatDetailHeader showArchive showNew />
        <MessageListContent ref={contentRef}>
          <MessageList
            messages={messages}
            isRunning={isRunning}
            isEscalated={escalated}
            escalationBannerIndex={escalationBannerIndex}
            prompts={prompts}
            onRetry={handleRetry}
            onSelectPromptOption={handleSelectPromptOption}
            onStartNewConversation={startNewConversation}
          />
        </MessageListContent>
        <ComposerCardArea ref={composerRef}>
          {isOffline && (
            <OfflineIndicator>
              {translate("leapChat.error.offline" as TranslationKey)}
            </OfflineIndicator>
          )}
          <ChatComposer
            onSend={handleSend}
            inputValue={inputValue}
            onInputChange={cubit.setInputValue}
            isRunning={isRunning}
            attachments={attachments}
            onAddFile={cubit.addAttachment}
            onRemoveAttachment={cubit.removeAttachment}
          />
        </ComposerCardArea>
      </LeapChatViewport>
    </LeapChatContainer>
  );
};

export default ConversationDetailView;
